import React, { useEffect, useState, useRef, useLayoutEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, ChevronRight, ChevronLeft } from 'lucide-react';
import { useTheme } from '../../contexts/ThemeContext';
export interface TourStep {
  id: string;
  title: string;
  description: string;
  spotlightSelector: string;
  /** Preferred placement of the card relative to the highlight */
  prefer: 'above' | 'below';
  highlightPadding?: number;
}
interface OnboardingTourProps {
  isActive: boolean;
  steps: TourStep[];
  onComplete: () => void;
  onSkip: () => void;
}
/** Geometry of the highlight in the phone's own (unscaled) coordinate space */
interface Rect {
  left: number;
  top: number;
  width: number;
  height: number;
}
const CARD_HEIGHT_EST = 196;
const GAP = 14;
export function OnboardingTour({
  isActive,
  steps,
  onComplete,
  onSkip
}: OnboardingTourProps) {
  const {
    isLight
  } = useTheme();
  const containerRef = useRef<HTMLDivElement>(null);
  const [currentStep, setCurrentStep] = useState(0);
  const [rect, setRect] = useState<Rect | null>(null);
  const step = steps[currentStep];
  const isFirstStep = currentStep === 0;
  const isLastStep = currentStep === steps.length - 1;
  const padding = step?.highlightPadding ?? 12;
  // Reset to the first step each time the tour opens or the step set changes
  useEffect(() => {
    if (isActive) {
      setCurrentStep(0);
      setRect(null);
    }
  }, [isActive, steps]);
  // Measure the target relative to the phone container (accounting for the
  // MobileFrame's scale() transform), re-running on step change + resize.
  useLayoutEffect(() => {
    if (!isActive || !step) return;
    let cancelled = false;
    let attempts = 0;
    const measure = () => {
      if (cancelled) return;
      const container = containerRef.current;
      const el = document.querySelector(step.spotlightSelector) as HTMLElement | null;
      if (!container || !el) {
        if (attempts++ < 25) setTimeout(measure, 60);
        return;
      }
      // Bring the target into view first (lists scroll vertically)
      el.scrollIntoView({
        block: 'center',
        behavior: 'smooth'
      });
      // Wait for the scroll to settle, then measure
      setTimeout(() => {
        if (cancelled) return;
        const cRect = container.getBoundingClientRect();
        const scale = cRect.width / (container.offsetWidth || cRect.width) || 1;
        const tRect = el.getBoundingClientRect();
        setRect({
          left: (tRect.left - cRect.left) / scale,
          top: (tRect.top - cRect.top) / scale,
          width: tRect.width / scale,
          height: tRect.height / scale
        });
      }, 280);
    };
    measure();
    window.addEventListener('resize', measure);
    return () => {
      cancelled = true;
      window.removeEventListener('resize', measure);
    };
  }, [isActive, step]);
  const handleNext = () => {
    if (isLastStep) onComplete();else setCurrentStep(p => p + 1);
  };
  const handleBack = () => {
    if (!isFirstStep) setCurrentStep(p => p - 1);
  };
  if (!isActive || !step) return null;
  const ch = containerRef.current?.offsetHeight ?? 844;
  const cw = containerRef.current?.offsetWidth ?? 390;
  const hx = rect ? rect.left - padding : 0;
  const hy = rect ? rect.top - padding : 0;
  const hw = rect ? rect.width + padding * 2 : 0;
  const hh = rect ? rect.height + padding * 2 : 0;
  // Decide card placement: honor preference but flip if it would clip
  let cardTop = 0;
  if (rect) {
    const below = hy + hh + GAP;
    const above = hy - GAP - CARD_HEIGHT_EST;
    if (step.prefer === 'below') {
      cardTop = below + CARD_HEIGHT_EST + 24 < ch ? below : Math.max(GAP, above);
    } else {
      cardTop = above > 24 ? above : below;
    }
    cardTop = Math.min(Math.max(cardTop, GAP + 36), ch - CARD_HEIGHT_EST - GAP);
  }
  return <div ref={containerRef} className="absolute inset-0 z-[80] overflow-hidden" data-id="element-1197" data-mp="20or1qp7vm4bl">
      {/* Dim layer with a rounded spotlight cutout */}
      <svg className="absolute inset-0 w-full h-full pointer-events-auto" data-id="element-1198" data-mp="2396w2llg63yd">
        <defs data-id="element-1199" data-mp="23bje0yr7gebf">
          <mask id="vexu-tour-mask" data-id="element-1200" data-mp="1yadikt65ucy7">
            <rect x="0" y="0" width="100%" height="100%" fill="white" data-id="element-1201" data-mp="1ybf3dpczadt4" />
            {rect && <motion.rect initial={false} animate={{
            x: hx,
            y: hy,
            width: hw,
            height: hh
          }} transition={{
            type: 'spring',
            stiffness: 320,
            damping: 32
          }} rx={18} fill="black" data-id="element-1202" data-mp="2d8nz0v9be3qn" />}
          </mask>
        </defs>
        <rect x="0" y="0" width="100%" height="100%" fill="black" opacity={isLight ? 0.55 : 0.68} mask="url(#vexu-tour-mask)" data-id="element-1203" data-mp="2qzb0uwadg9qy" />
      </svg>

      {/* Glow ring around the highlight */}
      {rect && <motion.div className="absolute pointer-events-none rounded-[18px]" initial={false} animate={{
      left: hx,
      top: hy,
      width: hw,
      height: hh
    }} transition={{
      type: 'spring',
      stiffness: 320,
      damping: 32
    }} style={{
      boxShadow: `0 0 0 1.5px rgba(0,200,83,0.55), 0 0 22px 4px rgba(0,200,83,${isLight ? 0.18 : 0.28})`
    }} data-id="element-1204" data-mp="3jlhqbgtno68a" />}

      {/* Coach-mark card */}
      <AnimatePresence mode="wait" data-id="element-1205" data-mp="35mrqhtfgbsg8">
        {rect && <motion.div key={step.id} initial={{
        opacity: 0,
        y: 12
      }} animate={{
        opacity: 1,
        y: 0
      }} exit={{
        opacity: 0,
        y: -8
      }} transition={{
        type: 'spring',
        stiffness: 360,
        damping: 30
      }} className="absolute left-4 right-4 pointer-events-auto" style={{
        top: cardTop,
        width: cw - 32
      }} data-id="element-1206" data-mp="2rubnutqgpwfu">
            <div className={`rounded-3xl p-5 shadow-2xl backdrop-blur-2xl border ${isLight ? 'bg-white/95 border-slate-200/70 shadow-black/10' : 'bg-[#161616]/95 border-white/10 shadow-black/50'}`} data-id="element-1207" data-mp="jmq0tasblvvs">
              <div className="flex items-start justify-between gap-3 mb-3" data-id="element-1208" data-mp="1be5vls416ox3">
                <div className="flex-1 min-w-0" data-id="element-1209" data-mp="17ckaxkl996cc">
                  <h3 className={`text-base font-bold mb-1 ${isLight ? 'text-slate-900' : 'text-white'}`} data-id="element-1210" data-mp="17csmn95n65ly">
                    {step.title}
                  </h3>
                  <p className={`text-[13px] leading-relaxed ${isLight ? 'text-slate-600' : 'text-zinc-400'}`} data-id="element-1211" data-mp="xdled0drl15p">
                    {step.description}
                  </p>
                </div>
                <button onClick={onSkip} aria-label="Skip tour" className={`p-1.5 rounded-full flex-shrink-0 transition-colors ${isLight ? 'text-slate-400 hover:bg-slate-100 hover:text-slate-600' : 'text-zinc-500 hover:bg-white/10 hover:text-zinc-300'}`} data-id="element-1212" data-mp="380f5y43qsept">
                  <X className="w-4 h-4" data-id="element-1213" data-mp="3mpjmzhyswcwc" />
                </button>
              </div>

              {/* Progress */}
              <div className="flex items-center gap-1.5 mb-4" data-id="element-1214" data-mp="3qq3jqkfgpehy">
                {steps.map((_, index) => <div key={index} className={`h-1.5 rounded-full transition-all duration-300 ${index === currentStep ? 'w-6 bg-vexu' : index < currentStep ? 'w-1.5 bg-vexu/40' : isLight ? 'w-1.5 bg-slate-200' : 'w-1.5 bg-white/10'}`} data-id="element-1215" data-mp="3voetkv5rbp06" data-mpi={index} />)}
              </div>

              <div className="flex items-center gap-2.5" data-id="element-1216" data-mp="26dee831tlblm">
                {!isFirstStep ? <button onClick={handleBack} className={`flex items-center gap-1 px-4 py-2.5 rounded-xl text-sm font-semibold transition-colors ${isLight ? 'bg-slate-100 text-slate-700 hover:bg-slate-200' : 'bg-white/5 text-zinc-300 hover:bg-white/10'}`} data-id="element-1217" data-mp="2bbzeu3kzhxal">
                    <ChevronLeft className="w-4 h-4" data-id="element-1218" data-mp="lt986qlhtjd7" />
                    Back
                  </button> : <button onClick={onSkip} className={`px-4 py-2.5 rounded-xl text-sm font-medium transition-colors ${isLight ? 'text-slate-400 hover:text-slate-600' : 'text-zinc-500 hover:text-zinc-300'}`} data-id="element-1219" data-mp="3u814aacx4vzv">
                    Skip
                  </button>}
                <button onClick={handleNext} className="flex-1 flex items-center justify-center gap-1.5 px-4 py-2.5 rounded-xl text-sm font-bold bg-vexu text-white hover:bg-vexu-600 transition-colors" data-id="element-1220" data-mp="3181x36e2qtr">
                  {isLastStep ? 'Get Started' : 'Next'}
                  {!isLastStep && <ChevronRight className="w-4 h-4" data-id="element-1221" data-mp="1r0ahvmocsze2" />}
                </button>
              </div>

              <p className={`text-center text-[11px] mt-3 ${isLight ? 'text-slate-400' : 'text-zinc-500'}`} data-id="element-1222" data-mp="1c31bblqhbp1r">
                {currentStep + 1} of {steps.length}
              </p>
            </div>
          </motion.div>}
      </AnimatePresence>
    </div>;
}